<template>
  <div :data-view-id="componentUniqueTitle || uniqueTitle">
  <b-container fluid class="chart-container">
    <ViewTitle :title="title" extra-class="chart-title" />
    <b-row v-if="visiblePresetFilterButtons.length" class="mb-3">
      <b-col>
        <PresetFilterButtons
          :buttons="visiblePresetFilterButtons"
          :active-ids="activePresetFilterIds"
          @toggle="onPresetFilterToggle"
        />
      </b-col>
    </b-row>
    <b-row v-if="userFiltering" class="mb-3">
      <b-col>
        <RuntimeFilterBuilder
          ref="filterBuilder"
          :field-groups="filterFieldGroups"
          :initial-filters="resolvedInitialFilters"
          :initial-match-mode="resolvedInitialMatchMode"
          @filter-changed="onFilterChanged"
        />
      </b-col>
    </b-row>
    <div class="chart-wrapper" ref="chartWrapper">
      <component
        v-if="chartComponent && chartData"
        :is="chartComponent"
        :data="chartData"
        :options="chartOptions"
        ref="chartRef"
      />
      <div v-if="!chartData" class="text-center text-muted py-5">
        Loading chart data...
      </div>
    </div>
    <details v-if="chartData && chartData.labels && chartData.labels.length" class="chart-data-table">
      <summary>View data</summary>
      <table>
        <caption class="visually-hidden">{{ title || 'Chart data' }}</caption>
        <thead>
          <tr>
            <th scope="col">Label</th>
            <th v-for="(dataset, datasetIndex) in chartData.datasets" :key="datasetIndex" scope="col">{{ dataset.label || 'Value' }}</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(label, index) in chartData.labels" :key="index">
            <th scope="row">{{ label }}</th>
            <td v-for="(dataset, datasetIndex) in chartData.datasets" :key="datasetIndex">{{ dataset.data[index] }}</td>
          </tr>
        </tbody>
      </table>
    </details>
  </b-container>
  </div>
</template>

<script>
import axios from 'axios'
import RuntimeFilterBuilder from './RuntimeFilterBuilder.vue'
import ViewTitle from './ViewTitle.vue'
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  ArcElement,
  RadialLinearScale,
  Title,
  Tooltip,
  Legend,
  Filler
} from 'chart.js'
import { Bar, Line, Pie, Doughnut, Radar, PolarArea } from 'vue-chartjs'
import { realtimeSubscribeFields } from '../lib/view-visibility'
import { liveAppWebSocketUrl } from '../lib/live-app-socket'
import { eventRefreshesView } from '../../../shared/view-refresh'
import { filterQueryHref, readFilterQuery } from '../lib/filter-query'
import PresetFilterButtons from './PresetFilterButtons.vue'
import { presetFilterMixin } from '../lib/preset-filter-state'

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  ArcElement,
  RadialLinearScale,
  Title,
  Tooltip,
  Legend,
  Filler
)

const chartComponentMap = {
  bar: Bar,
  line: Line,
  pie: Pie,
  doughnut: Doughnut,
  radar: Radar,
  polarArea: PolarArea
}

const DEFAULT_COLORS = [
  'rgba(54, 162, 235, 0.7)',
  'rgba(255, 99, 132, 0.7)',
  'rgba(75, 192, 192, 0.7)',
  'rgba(255, 206, 86, 0.7)',
  'rgba(153, 102, 255, 0.7)',
  'rgba(255, 159, 64, 0.7)',
  'rgba(46, 204, 113, 0.7)',
  'rgba(231, 76, 60, 0.7)',
  'rgba(52, 152, 219, 0.7)',
  'rgba(241, 196, 15, 0.7)'
]

const DEFAULT_BORDER_COLORS = DEFAULT_COLORS.map(c => c.replace('0.7)', '1)'))

function computeLinearTrendLine (values) {
  const n = values.length
  if (n < 2) return values.slice()
  let sumX = 0; let sumY = 0; let sumXY = 0; let sumX2 = 0
  for (let i = 0; i < n; i++) {
    sumX += i
    sumY += values[i]
    sumXY += i * values[i]
    sumX2 += i * i
  }
  const denom = n * sumX2 - sumX * sumX
  if (denom === 0) return values.slice()
  const slope = (n * sumXY - sumX * sumY) / denom
  const intercept = (sumY - slope * sumX) / n
  return values.map((_, i) => Math.round((slope * i + intercept) * 100) / 100)
}

export default {
  mixins: [presetFilterMixin],
  components: {
    RuntimeFilterBuilder,
    ViewTitle,
    PresetFilterButtons
  },
  data () {
    return {
      chartData: null,
      labelFieldLabel: null,
      filterFieldGroups: [],
      activeFilters: null,
      urlFilters: null,
      urlMatchMode: null,
      ws: null
    }
  },
  props: {
    uniqueTitle: { type: String, default: '' },
    title: { type: String, default: '' },
    chartType: { type: String, default: 'bar' },
    userFiltering: { type: Boolean, default: false },
    realtime: { type: Boolean, default: false },
    spreadSheetUniqueTitle: { type: String, default: '' },
    pageUniqueTitle: { type: String, default: '' },
    componentUniqueTitle: { type: String, default: '' },
    defaultUserFilters: { type: Array, default: () => [] },
    defaultUserFilterMatchMode: { type: String, default: 'all' },
    restrictUserFiltering: { type: Boolean, default: false }
  },
  computed: {
    chartComponent () {
      return chartComponentMap[this.chartType] || Bar
    },
    chartOptions () {
      const isRadial = this.chartType === 'pie' || this.chartType === 'doughnut' || this.chartType === 'polarArea'
      const hasMultipleDatasets = this.chartData && this.chartData.datasets && this.chartData.datasets.length > 1
      const opts = {
        responsive: true,
        maintainAspectRatio: true,
        plugins: {
          legend: {
            display: isRadial || hasMultipleDatasets
          }
        }
      }
      if (!isRadial && this.labelFieldLabel) {
        opts.scales = {
          x: {
            title: {
              display: true,
              text: this.labelFieldLabel
            }
          }
        }
      }
      return opts
    },
    filterQueryKey () {
      return `f_${this.componentUniqueTitle || this.uniqueTitle}`
    },
    resolvedInitialFilters () {
      if (this.urlFilters) return this.urlFilters
      return this.defaultUserFilters
    },
    resolvedInitialMatchMode () {
      if (this.urlMatchMode) return this.urlMatchMode
      return this.defaultUserFilterMatchMode
    }
  },
  mounted () {
    this.setupResizeObserver()
    if (window.__superturbo_preview_mode) {
      this.loadChartData()
      return
    }
    if (this.userFiltering) {
      this.readFiltersFromUrl()
      this.loadFilterFields()
    }
    this.loadChartData()
    if (this.realtime) {
      this.connectRealtimeWebSocket()
    }
    this._onRefreshViews = (event) => {
      if (!eventRefreshesView(event, this.componentUniqueTitle || this.uniqueTitle)) return
      this.loadChartData(this.activeFilters)
    }
    window.addEventListener('superturbo-refresh-views', this._onRefreshViews)
  },
  beforeUnmount () {
    this.disconnectRealtimeWebSocket()
    this.cleanupResizeObserver()
    if (this._onRefreshViews) {
      window.removeEventListener('superturbo-refresh-views', this._onRefreshViews)
    }
  },
  methods: {
    setupResizeObserver () {
      const wrapper = this.$refs.chartWrapper
      if (!wrapper) return
      let lastWidth = wrapper.clientWidth
      this._resizeRafId = null
      this._resizeObserver = new ResizeObserver(() => {
        const newWidth = wrapper.clientWidth
        if (newWidth !== lastWidth) {
          lastWidth = newWidth
          if (this._resizeRafId) cancelAnimationFrame(this._resizeRafId)
          this._resizeRafId = requestAnimationFrame(() => {
            this._resizeRafId = null
            const chartComp = this.$refs.chartRef
            if (chartComp && chartComp.chart) {
              chartComp.chart.resize()
            }
          })
        }
      })
      this._resizeObserver.observe(wrapper)
    },
    cleanupResizeObserver () {
      if (this._resizeRafId) {
        cancelAnimationFrame(this._resizeRafId)
        this._resizeRafId = null
      }
      if (this._resizeObserver) {
        this._resizeObserver.disconnect()
        this._resizeObserver = null
      }
    },
    buildChartData (labels, values, datasets) {
      const isRadial = this.chartType === 'pie' || this.chartType === 'doughnut' || this.chartType === 'polarArea'
      const isLine = this.chartType === 'line'
      const seriesList = datasets && datasets.length > 0
        ? datasets
        : [{ field: this.title || 'Data', label: this.title || 'Data', values: values || [] }]

      if (isRadial || seriesList.length === 1) {
        const series = seriesList[0]
        const data = series.values
        const result = {
          labels,
          datasets: [{
            label: this.title || series.label || series.field || 'Data',
            data,
            backgroundColor: isRadial
              ? labels.map((_, i) => DEFAULT_COLORS[i % DEFAULT_COLORS.length])
              : DEFAULT_COLORS[0],
            borderColor: isRadial
              ? labels.map((_, i) => DEFAULT_BORDER_COLORS[i % DEFAULT_BORDER_COLORS.length])
              : DEFAULT_BORDER_COLORS[0],
            borderWidth: 1
          }]
        }
        if (isLine && series.trendLine && data.length >= 2) {
          result.datasets.push({
            label: (this.title || series.label || series.field || 'Data') + ' (trend)',
            data: computeLinearTrendLine(data),
            borderColor: DEFAULT_BORDER_COLORS[0],
            borderWidth: 2,
            borderDash: [6, 4],
            pointRadius: 0,
            pointHoverRadius: 0,
            fill: false
          })
        }
        return result
      }

      const resultDatasets = []
      seriesList.forEach((series, idx) => {
        const color = DEFAULT_COLORS[idx % DEFAULT_COLORS.length]
        const borderColor = DEFAULT_BORDER_COLORS[idx % DEFAULT_BORDER_COLORS.length]
        resultDatasets.push({
          label: series.label || series.field,
          data: series.values,
          backgroundColor: color,
          borderColor: borderColor,
          borderWidth: 1
        })
        if (isLine && series.trendLine && series.values.length >= 2) {
          resultDatasets.push({
            label: (series.label || series.field) + ' (trend)',
            data: computeLinearTrendLine(series.values),
            borderColor: borderColor,
            borderWidth: 2,
            borderDash: [6, 4],
            pointRadius: 0,
            pointHoverRadius: 0,
            fill: false
          })
        }
      })

      return { labels, datasets: resultDatasets }
    },
    reloadForPresetFilters () {
      return this.loadChartData(this.activeFilters)
    },
    async loadChartData (filterEvent) {
      if (window.__superturbo_preview_mode) {
        const labels = ['Category A', 'Category B', 'Category C', 'Category D', 'Category E']
        const values = [42, 78, 55, 91, 63]
        this.chartData = this.buildChartData(labels, values, null)
        return
      }

      const pageUT = this.pageUniqueTitle || this.routePageUniqueTitle || window.__superturbo_getPageUniqueTitle?.()
      const compUT = this.componentUniqueTitle || this.uniqueTitle
      const account = this.routeAccount
      const application = this.$route?.params?.application || ''

      let url = `${VUE_APP_PROXY_SERVER_URL}/${account}/${application}/pages/${pageUT}/components/${compUT}/chartdata`

      const queryParts = []
      if (filterEvent && filterEvent.filters && filterEvent.filters.length > 0) {
        queryParts.push(`userFilters=${encodeURIComponent(JSON.stringify(filterEvent.filters))}`)
        queryParts.push(`userFilterMatchMode=${encodeURIComponent(filterEvent.matchMode || 'all')}`)
      }
      if (this.presetFilterQuery()) {
        queryParts.push(`presetFilters=${encodeURIComponent(this.presetFilterQuery())}`)
      }

      const accessKey = this.routeAccessKey
      if (accessKey) {
        queryParts.push(`accessKey=${encodeURIComponent(accessKey)}`)
      }

      if (queryParts.length > 0) {
        url += `?${queryParts.join('&')}`
      }

      try {
        const response = await axios.get(url)
        const { labels, values, datasets, labelFieldLabel } = response.data
        this.labelFieldLabel = labelFieldLabel || null
        this.chartData = this.buildChartData(labels, values, datasets)
      } catch (err) {
        console.warn('Failed to load chart data:', err)
      }
    },
    async loadFilterFields () {
      const pageUT = this.pageUniqueTitle || this.routePageUniqueTitle || window.__superturbo_getPageUniqueTitle?.()
      const compUT = this.componentUniqueTitle || this.uniqueTitle
      if (!pageUT || !compUT) return

      const account = this.routeAccount
      const application = this.$route?.params?.application || ''

      try {
        let url = `${VUE_APP_PROXY_SERVER_URL}/${account}/${application}/pages/${pageUT}/components/${compUT}/filterfields`
        if (this.restrictUserFiltering) {
          url += '?restrict=true'
        }
        const response = await axios.get(url)
        this.filterFieldGroups = response.data
      } catch (err) {
        console.warn('Failed to load filter fields:', err)
      }
    },
    compactFilter (filter) {
      const compact = {}
      for (const [k, v] of Object.entries(filter)) {
        if (v === null || v === undefined) continue
        if (k === 'valueFieldRef' && v !== null) {
          compact[k] = this.compactFilter(v)
        } else {
          compact[k] = v
        }
      }
      return compact
    },
    expandFilter (compact) {
      const defaults = {
        relationshipColumn: null,
        relatedSpreadsheet: null,
        sourceSpreadsheet: null,
        sourceRelationshipColumn: null,
        relQuantifier: null,
        valueType: 'literal',
        value: '',
        valueFieldRef: null
      }
      const full = { ...defaults, ...compact }
      if (full.valueFieldRef && typeof full.valueFieldRef === 'object') {
        full.valueFieldRef = this.expandFilter(full.valueFieldRef)
      }
      return full
    },
    filtersMatchDefaults (filterEvent) {
      const defaults = this.defaultUserFilters || []
      const current = (filterEvent && filterEvent.filters) || []
      const currentMode = (filterEvent && filterEvent.matchMode) || 'all'
      const defaultMode = this.defaultUserFilterMatchMode || 'all'
      if (currentMode !== defaultMode) return false
      if (current.length !== defaults.length) return false
      return JSON.stringify(current) === JSON.stringify(defaults)
    },
    readFiltersFromUrl () {
      const parsed = readFilterQuery(window.location.search, this.filterQueryKey)
      if (!parsed) return
      this.urlFilters = parsed.filters.map(f => this.expandFilter(f))
      this.urlMatchMode = parsed.matchMode
    },
    writeFiltersToUrl (filterEvent) {
      try {
        const hasFilters = filterEvent && filterEvent.filters && filterEvent.filters.length > 0
        let payload = null
        if (hasFilters && !this.filtersMatchDefaults(filterEvent)) {
          payload = { filters: filterEvent.filters.map(f => this.compactFilter(f)) }
          if ((filterEvent.matchMode || 'all') !== 'all') {
            payload.matchMode = filterEvent.matchMode
          }
        }
        window.history.replaceState(null, '', filterQueryHref(window.location.href, this.filterQueryKey, payload))
      } catch (err) {
        console.warn('Failed to write filters to URL:', err)
      }
    },
    async onFilterChanged (filterEvent) {
      this.activeFilters = filterEvent
      this.writeFiltersToUrl(filterEvent)
      this.loadChartData(filterEvent)
    },
    connectRealtimeWebSocket () {
      try {
        this.ws = new WebSocket(liveAppWebSocketUrl(VUE_APP_APP_SERVER_WS_URL, this.routeAccount, this.$route.params.application))
      } catch (err) {
        console.warn('Realtime WebSocket connection failed:', err)
        return
      }

      this.ws.onopen = () => {
        this.ws.send(JSON.stringify({
          type: 'realtime_subscribe',
          pageUniqueTitle: this.pageUniqueTitle,
          componentUniqueTitle: this.componentUniqueTitle,
          spreadSheetUniqueTitle: this.spreadSheetUniqueTitle,
          ...realtimeSubscribeFields({
            ...(this.$route && this.$route.params),
            account: this.routeAccount
          })
        }))
      }

      this.ws.onmessage = (event) => {
        let msg
        try {
          msg = JSON.parse(event.data)
        } catch { return }

        if (msg.type === 'realtime_update' &&
            msg.pageUniqueTitle === this.pageUniqueTitle &&
            msg.componentUniqueTitle === this.componentUniqueTitle) {
          if (this.activePresetFilterIds.length) {
            this.loadChartData(this.activeFilters)
            return
          }
          const { labels, values, datasets, labelFieldLabel } = msg.data
          this.labelFieldLabel = labelFieldLabel || null
          this.chartData = this.buildChartData(labels, values, datasets)
        }
      }

      this.ws.onclose = () => {
        setTimeout(() => {
          if (this.realtime && !this._isBeingDestroyed) {
            this.connectRealtimeWebSocket()
          }
        }, 3000)
      }

      this.ws.onerror = () => {}
    },
    disconnectRealtimeWebSocket () {
      if (this.ws) {
        if (this.ws.readyState === WebSocket.OPEN) {
          this.ws.send(JSON.stringify({
            type: 'realtime_unsubscribe',
            pageUniqueTitle: this.pageUniqueTitle,
            componentUniqueTitle: this.componentUniqueTitle
          }))
        }
        this.ws.onclose = null
        this.ws.close()
        this.ws = null
      }
    }
  }
}
</script>

<style scoped>
.chart-container {
  padding: 1rem 0;
}

.chart-wrapper {
  position: relative;
  width: 100%;
  max-width: 800px;
  margin: 0 auto;
}
</style>
